import { DateTime } from '@ksyos/date-time';
import { Box, Button, Checkbox, FormErrorMessage, FormGroup, Modal, Text } from '@ksyos/design-system';
import { cloneDeep } from 'lodash';
import { useEffect, useState } from 'react';
import type { OrganizationTypeWithLinkedProfessions } from '@common/enums';
import type { AccountRequestAddValidated } from '@common/validation/accountRequest';
import type { EmploymentAdd, PractitionerEmployment } from '@frontend/components/elements/AccountCreationModalFields';
import type { IOption } from '@frontend/components/interfaces/option';
import type { ValidationErrors } from '@frontend/utils';
import { Profession, ValidationErrorCode } from '@common/enums';
import { MatchingEntityResultCode } from '@common/enums/matchingEntityResultCode';
import { convertOrganizationTypeToOrganizationTypeWithLinkedProfessions } from '@common/utils/convertOrganizationTypeToOrganizationTypeWithLinkedProfessions';
import { padStartBigCode } from '@common/utils/padStartBigCode';
import { accountRequestAddSchema } from '@common/validation/accountRequest';
import {
    AccountCreationModalFields,
    emptyEmploymentAdd,
    emptyPractitionerAdd,
} from '@frontend/components/elements/AccountCreationModalFields';
import { IndependentDateInput } from '@frontend/components/fields';
import { createAccountRequest } from '@frontend/services/api/accountRequestsApiService';
import { getOneOrganization } from '@frontend/services/api/organizationsApiService';
import { fetchJson } from '@frontend/store/fetch';
import { getSelectedLanguage } from '@frontend/translate';
import {
    emptyValidationErrors,
    getValidationErrorTranslation,
    updateValidationErrorsForMany,
    updateValidationErrorsForOne,
    validate,
} from '@frontend/utils';
import { useAbortSignal } from '@frontend/utils/hooks/useAbortSignal';
import { AccountRequestSelectApprover } from './AccountRequestSelectApprover';

interface ExtendedEmploymentAdd extends EmploymentAdd {
    startDate: string | null;
}

interface AccountAdd extends PractitionerEmployment {
    approverPractitionerId: string | null;
    employment: ExtendedEmploymentAdd;
    isUnderstandingChecked: boolean;
    matchingAccountRequestId: string | null;
    matchingPractitionerHasEmailAddress: boolean | null;
    matchingResultCode: MatchingEntityResultCode;
    organizationType: OrganizationTypeWithLinkedProfessions | null;
    practitionerId: string | null;
}

interface AccountRequestsAddProps {
    isOpen: boolean;
    onClose: (newAccountRequestCreated?: boolean) => void;
    organizationId: string;
}

const emptyExtendedEmploymentAdd: ExtendedEmploymentAdd = {
    ...emptyEmploymentAdd,
    startDate: DateTime.now().format('YYYY-MM-DD'),
};

export function AccountRequestAdd(props: Readonly<AccountRequestsAddProps>) {
    const abortSignal = useAbortSignal();
    const [newAccountRequest, setNewAccountRequest] = useState<AccountAdd>({
        approverPractitionerId: null,
        employment: emptyExtendedEmploymentAdd,
        isUnderstandingChecked: false,
        matchingAccountRequestId: null,
        matchingPractitionerHasEmailAddress: null,
        matchingResultCode: MatchingEntityResultCode.NO_MATCH_FOUND,
        organizationType: null,
        practitioner: emptyPractitionerAdd,
        practitionerId: null,
    });
    const [errors, setErrors] = useState<ValidationErrors>(emptyValidationErrors);
    const [isSaving, setIsSaving] = useState<boolean>(false);
    const [approverPractitionerOptions, setApproverPractitionerOptions] = useState<IOption<string>[]>([]);
    const [organizationType, setOrganizationType] = useState<OrganizationTypeWithLinkedProfessions | null>(null);
    const [needsKsyosApproval, setNeedsKsyosApproval] = useState<boolean>(false);

    const translate = getSelectedLanguage();

    useEffect(() => {
        async function fetchData() {
            const approverOptions = await getApproverPractitionerOptions();
            const organizationTypeResult = await getOrganizationType();

            setApproverPractitionerOptions(approverOptions);
            setOrganizationType(organizationTypeResult);
            setNewAccountRequest({ ...newAccountRequest, organizationType: organizationTypeResult });
            setNeedsKsyosApproval(approverOptions.length === 0);
        }

        fetchData();
    }, []);

    if (!props.organizationId) {
        return null;
    }

    return (
        <Modal
            isOpen={props.isOpen}
            onClose={() => props.onClose()}
            size="large"
        >
            <Modal.Header>{translate.accountRequest.addCareProvider}</Modal.Header>
            <Modal.Body>
                <AccountCreationModalFields
                    errors={errors}
                    handleChangeNewAccount={(newAccount: PractitionerEmployment) => {
                        handleChangeNewAccount({
                            ...newAccount,
                            employment: { ...newAccount.employment, startDate: newAccountRequest.employment.startDate },
                        });
                    }}
                    handleSetExistingId={(accountRequestMatch, newAccountFields) => {
                        accountRequestMatch
                            ? handleChangeNewAccount({
                                  ...newAccountFields,
                                  [accountRequestMatch.entity]: accountRequestMatch.id,
                                  employment: {
                                      ...newAccountFields!.employment,
                                      startDate: newAccountRequest.employment.startDate,
                                  },
                                  matchingPractitionerHasEmailAddress: accountRequestMatch.hasPractitionerEmailAddress,
                              })
                            : handleChangeNewAccount({
                                  ...newAccountFields,
                                  employment: {
                                      ...newAccountFields!.employment,
                                      startDate: newAccountRequest.employment.startDate,
                                  },
                                  matchingAccountRequestId: null,
                                  matchingPractitionerHasEmailAddress: null,
                                  practitionerId: null,
                              });
                    }}
                    organizationId={props.organizationId}
                    organizationType={organizationType}
                    validateMany={(
                        keys: string[],
                        updatedNewAccount?: PractitionerEmployment & { matchingResultCode: MatchingEntityResultCode }
                    ) =>
                        validateMany(
                            keys,
                            updatedNewAccount
                                ? {
                                      ...updatedNewAccount,
                                      approverPractitionerId: newAccountRequest.approverPractitionerId,
                                      employment: {
                                          ...updatedNewAccount.employment,
                                          startDate: newAccountRequest.employment.startDate,
                                      },
                                      isUnderstandingChecked: newAccountRequest.isUnderstandingChecked,
                                      matchingAccountRequestId: newAccountRequest.matchingAccountRequestId,
                                      matchingPractitionerHasEmailAddress:
                                          newAccountRequest.matchingPractitionerHasEmailAddress,
                                      matchingResultCode: newAccountRequest.matchingResultCode,
                                      needsKsyosApproval,
                                      organizationType: newAccountRequest.organizationType,
                                      practitionerId: newAccountRequest.practitionerId,
                                  }
                                : undefined
                        )
                    }
                    validateOne={(
                        key: string,
                        updatedNewAccount?: PractitionerEmployment & { matchingResultCode: MatchingEntityResultCode }
                    ) =>
                        validateOne(
                            key,
                            updatedNewAccount
                                ? {
                                      ...updatedNewAccount,
                                      approverPractitionerId: newAccountRequest.approverPractitionerId,
                                      employment: {
                                          ...updatedNewAccount.employment,
                                          startDate: newAccountRequest.employment.startDate,
                                      },
                                      isUnderstandingChecked: newAccountRequest.isUnderstandingChecked,
                                      matchingAccountRequestId: newAccountRequest.matchingAccountRequestId,
                                      matchingPractitionerHasEmailAddress:
                                          newAccountRequest.matchingPractitionerHasEmailAddress,
                                      matchingResultCode: newAccountRequest.matchingResultCode,
                                      needsKsyosApproval,
                                      organizationType: newAccountRequest.organizationType,
                                      practitionerId: newAccountRequest.practitionerId,
                                  }
                                : undefined
                        )
                    }
                    willPractitionerUseCS={true}
                />
                <FormGroup
                    hasErrors={errors.has('employment.startDate')}
                    isRequired
                    label={translate.accountRequest.startDate}
                >
                    <IndependentDateInput
                        error={errors.get('employment.startDate')}
                        hasMaxWidth={true}
                        minDate={DateTime.now().format()}
                        onChange={(value) => handleChangeEmployment({ startDate: value })}
                        onDoValidate={(value) =>
                            validateOne('employment.startDate', {
                                ...newAccountRequest,
                                employment: {
                                    ...newAccountRequest.employment,
                                    startDate: value,
                                },
                            })
                        }
                        placeholder={translate.common.datePlaceholder}
                        size="large"
                        value={newAccountRequest.employment.startDate}
                        variant="outline"
                    />
                </FormGroup>
                {approverPractitionerOptions.length > 0 &&
                    newAccountRequest.matchingResultCode !== MatchingEntityResultCode.NAME_GENDER_MATCH && (
                        <AccountRequestSelectApprover
                            approverOptions={approverPractitionerOptions}
                            errors={errors}
                            onChange={(approverPractitionerId) => {
                                handleChangeNewAccount({
                                    approverPractitionerId,
                                });
                                validateOne('approverPractitionerId', {
                                    ...newAccountRequest,
                                    approverPractitionerId,
                                    needsKsyosApproval,
                                });
                            }}
                            organizationId={props.organizationId}
                            selectedApproverId={newAccountRequest.approverPractitionerId}
                            showHelpText={true}
                        />
                    )}

                <FormGroup
                    hasErrors={errors.has('isUnderstandingChecked')}
                    layout="vertical"
                >
                    <Box
                        alignItems={['flex-start', 'flex-start']}
                        display="flex"
                        gap={[2, 2]}
                    >
                        <Checkbox
                            checked={newAccountRequest.isUnderstandingChecked}
                            onChange={(e) => {
                                handleChangeNewAccount({ isUnderstandingChecked: e.target.checked });
                                validateOne('isUnderstandingChecked', {
                                    ...newAccountRequest,
                                    isUnderstandingChecked: e.target.checked,
                                });
                            }}
                        />

                        <Text>
                            <b>{translate.common.yes}</b>, {translate.accountRequest.understandingText}
                        </Text>
                    </Box>
                    {errors.has('isUnderstandingChecked') && (
                        <FormErrorMessage>
                            {getValidationErrorTranslation(errors.get('isUnderstandingChecked'))}
                        </FormErrorMessage>
                    )}
                </FormGroup>
            </Modal.Body>
            <Modal.Footer>
                <Button
                    onClick={() => props.onClose()}
                    variant="outline"
                >
                    {translate.common.cancel}
                </Button>
                <Button
                    onClick={async () => {
                        await handleSubmit();
                    }}
                >
                    {translate.common.addTo}
                </Button>
            </Modal.Footer>
        </Modal>
    );

    function handleChangeEmployment(employmentUpdate: Partial<ExtendedEmploymentAdd>) {
        setNewAccountRequest({
            ...newAccountRequest,
            employment: { ...newAccountRequest.employment, ...employmentUpdate },
        });
    }

    function handleChangeNewAccount(newAccountUpdate: Partial<AccountAdd>) {
        if (
            newAccountUpdate.employment?.profession &&
            Object.values(Profession).includes(newAccountUpdate.employment.profession) &&
            newAccountUpdate.employment.profession !== newAccountRequest.employment.profession
        ) {
            setErrors(emptyValidationErrors);
        }

        setNewAccountRequest((current) => {
            if (newAccountUpdate.matchingResultCode === MatchingEntityResultCode.NAME_GENDER_MATCH) {
                return { ...current, ...newAccountUpdate, approverPractitionerId: null };
            } else {
                return { ...current, ...newAccountUpdate };
            }
        });
    }

    function validateOne(key: string, updatedNewAccount?: AccountAdd & { needsKsyosApproval?: boolean }) {
        const validationErrors = updateValidationErrorsForOne(
            accountRequestAddSchema,
            updatedNewAccount ?? newAccountRequest,
            errors,
            key
        );
        setErrors(validationErrors);
    }

    function validateMany(keys: string[], updatedNewAccount?: AccountAdd & { needsKsyosApproval?: boolean }) {
        const validationErrors = updateValidationErrorsForMany(
            accountRequestAddSchema,
            updatedNewAccount ?? newAccountRequest,
            errors,
            keys
        );
        setErrors(validationErrors);
    }

    async function getOrganizationType() {
        const organization = await getOneOrganization(props.organizationId, abortSignal);
        const organizationTypeWithLinkedProfessions = convertOrganizationTypeToOrganizationTypeWithLinkedProfessions(
            organization.organizationType!
        );

        return organizationTypeWithLinkedProfessions;
    }

    async function handleSubmit() {
        if (!isSaving) {
            setIsSaving(true);
            const hasMatchedOnNameAndGender =
                newAccountRequest.matchingResultCode === MatchingEntityResultCode.NAME_GENDER_MATCH;

            const newAccountRequestToSubmit = {
                ...cloneDeep(newAccountRequest),
                hasMatchedOnNameAndGender,
                needsKsyosApproval: hasMatchedOnNameAndGender ? true : needsKsyosApproval,
                organizationType,
            };

            const { errors: validationErrors } = validate(accountRequestAddSchema, newAccountRequestToSubmit);

            if (validationErrors.size > 0) {
                setErrors(validationErrors);
            } else {
                setErrors(emptyValidationErrors);
                const paddedIdentifierRegistrationBig = padStartBigCode(
                    newAccountRequestToSubmit.practitioner.identifierRegistrationBig
                ) as string | null;
                newAccountRequestToSubmit.practitioner.identifierRegistrationBig = paddedIdentifierRegistrationBig;

                const validationErrorCode = await createAccountRequest(
                    newAccountRequestToSubmit as AccountRequestAddValidated,
                    props.organizationId,
                    abortSignal
                );

                if (validationErrorCode === ValidationErrorCode.IDENTIFIER_DECLARATION_EXISTS) {
                    setErrors(errors.set('practitioner.identifierDeclaration', validationErrorCode));
                } else {
                    setErrors(emptyValidationErrors);
                    props.onClose(true);
                }
            }

            setIsSaving(false);
        }
    }

    async function getApproverPractitionerOptions() {
        const rawOptions = (
            await fetchJson(`/api/accountRequests/organization/${props.organizationId}/approvers`, 'GET')
        ).json.items;

        const options: IOption<string>[] = rawOptions.map((approver: { displayName: string; id: string }) => ({
            label: approver.displayName,
            value: approver.id,
        }));

        return options;
    }
}
